// ============================================================================
// CONFIG HOOKS
// ============================================================================
// React hooks for the configuration framework.
// No memory caching: each consumer fetches on mount, and refetch is exposed
// so consumers (modal/screen) can refresh when re-opened.
//
// General-purpose hooks:
//   useConfigGroups()                      → list all active config groups
//   useConfigDefinitions(group)            → on-demand fetch for a single group
//   useEffectiveConfig()                   → on-demand cascade-resolved values
//   useConfigScopes(scopeType)             → list scope rows (e.g., for OFFICE override dropdown)
//
// Typed convenience hooks (parsed values):
//   useTimesheetPolicy(officeId?, employeeId?)   → typed timesheet config
//   useSystemDefaults()                          → typed system defaults
//   useEngagementPolicy()                        → typed engagement policy

import { useCallback, useEffect, useMemo, useState } from 'react';
import { configService } from 'api/services/configService';
import type {
  ConfigDefinition,
  ConfigEffectiveValue,
  ConfigGroup,
  ConfigScopeRef,
  ConfigScopeRow,
  UpdateConfigRequest,
} from 'api/types';

// ============================================================
// LIST HOOKS
// ============================================================
export const useConfigGroups = () => {
  const [data, setData] = useState<ConfigGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await configService.getGroups();
      setData(response.data ?? []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
};

export const useConfigDefinitions = () => {
  const [data, setData] = useState<ConfigDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchDefinitions = useCallback(async (groupKey: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await configService.getDefinitions(groupKey);
      setData(response.data ?? []);
      return response.data ?? [];
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchDefinitions };
};

export const useEffectiveConfig = () => {
  const [data, setData] = useState<ConfigEffectiveValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchConfig = useCallback(async (groupKey: string, scopes?: ConfigScopeRef[]) => {
    setLoading(true);
    setError(null);
    try {
      const response = await configService.getEffectiveConfig(groupKey, scopes);
      setData(response.data ?? []);
      return response.data ?? [];
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchConfig };
};

export const useConfigScopes = (scopeType?: string) => {
  const [data, setData] = useState<ConfigScopeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!scopeType) {
      setData([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await configService.getScopes(scopeType);
      setData(response.data ?? []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [scopeType]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
};

export const useUpdateConfig = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const update = useCallback(async (request: UpdateConfigRequest) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await configService.updateValues(request);
      setSuccess(true);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { update, loading, error, success };
};

// ============================================================
// TYPED CONVENIENCE HOOKS — TIMESHEET POLICY
// ============================================================
export type DayOfWeek = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

export interface TimesheetPolicyConfig {
  weekStartDay: DayOfWeek;
  submissionDay: DayOfWeek;
  submissionCutoffHour: number;
  /** ISO date string YYYY-MM-DD */
  implementationStartDate: string;
  minDailyMinutes: number;
  defaultEmployee: boolean;
}

const TIMESHEET_DEFAULTS: TimesheetPolicyConfig = {
  weekStartDay: 'MONDAY',
  submissionDay: 'MONDAY',
  submissionCutoffHour: 12,
  implementationStartDate: '2026-04-01',
  minDailyMinutes: 480,
  defaultEmployee: true,
};

function findValue(values: ConfigEffectiveValue[], key: string): string | null | undefined {
  return values.find(v => v.configKey === key)?.effectiveValue;
}

/**
 * Typed timesheet policy with cascade resolution: SYSTEM → OFFICE → EMPLOYEE.
 * Falls back to seed defaults if the config endpoint is unavailable.
 *
 * Auto-fetches on mount and whenever officeId/employeeId changes.
 * Call refetch() in a modal's onOpen effect to ensure freshness.
 */
export const useTimesheetPolicy = (officeId?: string | null, employeeId?: string | null) => {
  const [config, setConfig] = useState<TimesheetPolicyConfig>(TIMESHEET_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const scopes: ConfigScopeRef[] = [];
      if (officeId) scopes.push({ type: 'OFFICE', id: officeId });
      if (employeeId) scopes.push({ type: 'EMPLOYEE', id: employeeId });

      const response = await configService.getEffectiveConfig('TIMESHEET_POLICY', scopes);
      const values = response.data ?? [];

      const weekStart = (findValue(values, 'timesheet_week_start_day') ?? 'MONDAY').toUpperCase() as DayOfWeek;
      const submissionDay = (findValue(values, 'timesheet_submission_day') ?? 'MONDAY').toUpperCase() as DayOfWeek;
      const cutoffHourRaw = findValue(values, 'timesheet_submission_cutoff_hour');
      const implStart = findValue(values, 'timesheet_implementation_start_date') ?? TIMESHEET_DEFAULTS.implementationStartDate;
      const minMinutesRaw = findValue(values, 'timesheet_min_daily_minutes');
      const defaultEmpRaw = findValue(values, 'timesheet_default_employee');

      setConfig({
        weekStartDay: weekStart,
        submissionDay: submissionDay,
        submissionCutoffHour: cutoffHourRaw ? parseInt(cutoffHourRaw, 10) : TIMESHEET_DEFAULTS.submissionCutoffHour,
        implementationStartDate: implStart,
        minDailyMinutes: minMinutesRaw ? parseInt(minMinutesRaw, 10) : TIMESHEET_DEFAULTS.minDailyMinutes,
        defaultEmployee: defaultEmpRaw ? defaultEmpRaw.toLowerCase() === 'true' || defaultEmpRaw === '1' : TIMESHEET_DEFAULTS.defaultEmployee,
      });
    } catch (err) {
      setError(err as Error);
      // Keep defaults so UI does not break if config endpoint is unavailable
    } finally {
      setLoading(false);
    }
  }, [officeId, employeeId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { config, loading, error, refetch: fetch };
};

// ============================================================
// TYPED CONVENIENCE HOOKS — SYSTEM DEFAULTS
// ============================================================
export interface SystemDefaultsConfig {
  defaultCurrency: string;
  defaultCountry: string;
  defaultTimezone: string;
  financialYearStartMonth: number;
}

const SYSTEM_DEFAULTS: SystemDefaultsConfig = {
  defaultCurrency: 'INR',
  defaultCountry: 'IN',
  defaultTimezone: 'Asia/Kolkata',
  financialYearStartMonth: 4,
};

export const useSystemDefaults = () => {
  const [config, setConfig] = useState<SystemDefaultsConfig>(SYSTEM_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await configService.getEffectiveConfig('SYSTEM_DEFAULTS');
      const values = response.data ?? [];
      const fyMonthRaw = findValue(values, 'financial_year_start_month');

      setConfig({
        defaultCurrency: findValue(values, 'default_currency') ?? SYSTEM_DEFAULTS.defaultCurrency,
        defaultCountry: findValue(values, 'default_country') ?? SYSTEM_DEFAULTS.defaultCountry,
        defaultTimezone: findValue(values, 'default_timezone') ?? SYSTEM_DEFAULTS.defaultTimezone,
        financialYearStartMonth: fyMonthRaw ? parseInt(fyMonthRaw, 10) : SYSTEM_DEFAULTS.financialYearStartMonth,
      });
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { config, loading, error, refetch: fetch };
};

// ============================================================
// TYPED CONVENIENCE HOOKS — ENGAGEMENT POLICY
// ============================================================
export interface EngagementPolicyConfig {
  closeRequiresAllTimesheetsSubmitted: boolean;
  defaultFeeType: string;
}

const ENGAGEMENT_DEFAULTS: EngagementPolicyConfig = {
  closeRequiresAllTimesheetsSubmitted: true,
  defaultFeeType: 'FIXED',
};

export const useEngagementPolicy = () => {
  const [config, setConfig] = useState<EngagementPolicyConfig>(ENGAGEMENT_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await configService.getEffectiveConfig('ENGAGEMENT_POLICY');
      const values = response.data ?? [];
      const closeRaw = findValue(values, 'engagement_close_requires_all_timesheets_submitted');
      setConfig({
        closeRequiresAllTimesheetsSubmitted: closeRaw
          ? closeRaw.toLowerCase() === 'true' || closeRaw === '1'
          : ENGAGEMENT_DEFAULTS.closeRequiresAllTimesheetsSubmitted,
        defaultFeeType: findValue(values, 'engagement_default_fee_type') ?? ENGAGEMENT_DEFAULTS.defaultFeeType,
      });
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { config, loading, error, refetch: fetch };
};

// ============================================================
// HELPERS — for UI that needs day-of-week ordering
// ============================================================
export const DAYS_OF_WEEK: { value: DayOfWeek; label: string; index: number }[] = [
  { value: 'MONDAY',    label: 'Monday',    index: 1 },
  { value: 'TUESDAY',   label: 'Tuesday',   index: 2 },
  { value: 'WEDNESDAY', label: 'Wednesday', index: 3 },
  { value: 'THURSDAY',  label: 'Thursday',  index: 4 },
  { value: 'FRIDAY',    label: 'Friday',    index: 5 },
  { value: 'SATURDAY',  label: 'Saturday',  index: 6 },
  { value: 'SUNDAY',    label: 'Sunday',    index: 0 },
];

export const dayIndexFromName = (day: DayOfWeek): number => {
  const found = DAYS_OF_WEEK.find(d => d.value === day);
  return found ? found.index : 1;
};

/**
 * Compute the first week-start date for a new employee.
 * Rule: max(implementation_start_date, employee.date_of_joining) → snap forward to weekStartDay.
 *
 * Returns YYYY-MM-DD in the caller's LOCAL timezone. We deliberately do NOT
 * use toISOString() here — toISOString returns UTC, so for any timezone east
 * of UTC (e.g. IST UTC+5:30) local midnight Apr 30 maps to UTC Apr 29 and the
 * slice would return the previous calendar day. Downstream snap-backward
 * logic in generateWeeks would then land a full week earlier than intended.
 */
export const computeEmployeeFirstWeekStart = (
  weekStartDay: DayOfWeek,
  implementationStartDate: string,
  dateOfJoining: string | null | undefined
): string => {
  const impl = new Date(implementationStartDate + 'T00:00:00');
  const join = dateOfJoining ? new Date(dateOfJoining + 'T00:00:00') : impl;
  const baseDate = join > impl ? join : impl;

  const targetDow = dayIndexFromName(weekStartDay); // 0=Sun..6=Sat
  const baseDow = baseDate.getDay();
  const offset = (targetDow - baseDow + 7) % 7;

  const result = new Date(baseDate);
  result.setDate(baseDate.getDate() + offset);

  const y = result.getFullYear();
  const m = String(result.getMonth() + 1).padStart(2, '0');
  const d = String(result.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Auto-export config alias to match existing useDesignation default
const useConfig = {
  useConfigGroups,
  useConfigDefinitions,
  useEffectiveConfig,
  useConfigScopes,
  useUpdateConfig,
  useTimesheetPolicy,
  useSystemDefaults,
  useEngagementPolicy,
};
export default useConfig;
